import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";

export function LeadDialog({ ctaType = "Request Information", triggerLabel, boat, variant = "purple", className = "", size = "default", testId }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const btnClass = variant === "purple"
    ? "bg-mtm-purple hover:bg-mtm-purple-deep text-white"
    : variant === "teal"
    ? "bg-mtm-teal hover:bg-mtm-teal-dark text-white"
    : "border border-mtm-purple text-mtm-purple bg-white hover:bg-mtm-purple hover:text-white";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || (!form.email && !form.phone)) {
      toast.error("Please enter your name and a phone or email.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", {
        ...form,
        boat_id: boat?.id,
        boat_title: boat ? `${boat.year} ${boat.brand} ${boat.model}` : undefined,
        stock_number: boat?.stock_number,
        originating_page: window.location.pathname,
        cta_type: ctaType,
      });
      toast.success("Thank you! The MTM team will be in touch shortly.");
      setForm({ name: "", phone: "", email: "", message: "" });
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className={`${btnClass} rounded-full ${className}`} size={size} data-testid={testId || "lead-dialog-trigger"}>
          {triggerLabel || ctaType}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="lead-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-mtm-purple-deep">{ctaType}</DialogTitle>
          <DialogDescription>
            {boat ? `Regarding ${boat.year} ${boat.brand} ${boat.model}${boat.stock_number ? ` (Stock #${boat.stock_number})` : ""}` : "Send us a message and we'll get right back to you."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2">
          <div>
            <Label htmlFor="lead-name">Name *</Label>
            <Input id="lead-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="lead-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="lead-phone">Phone</Label>
              <Input id="lead-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="lead-phone-input" />
            </div>
            <div>
              <Label htmlFor="lead-email">Email</Label>
              <Input id="lead-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="lead-email-input" />
            </div>
          </div>
          <div>
            <Label htmlFor="lead-message">Message</Label>
            <Textarea id="lead-message" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="lead-message-input" />
          </div>
          <Button type="submit" disabled={submitting} className="w-full bg-mtm-purple hover:bg-mtm-purple-deep text-white rounded-full" data-testid="lead-submit-button">
            {submitting ? "Sending..." : "Send Message"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
